import React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';

import { diffLines, TDiffLine } from './lineDiff';
import { formatJson } from './scriptFiles';

interface IScriptDiffDialogProps {
    open: boolean;
    title: string;
    /** the stored script; null while it loads */
    saved: string | null;
    /** the editor's text to be saved */
    next: string;
    /** products the overwrite reaches, when more than this one */
    sharedBy: number;
    onCancel: () => void;
    onConfirm: () => void;
}

/** JSON is compared re-indented, so the diff shows content, not whitespace. */
const normalize = (text: string): string => {
    try {
        return formatJson(JSON.parse(text));
    } catch {
        return text;
    }
};

const LINE_SX: Record<Exclude<TDiffLine['kind'], 'skip'>, object> = {
    same: { color: 'text.secondary' },
    // fixed green/red: the shop theme's success colour is a grey
    added: { bgcolor: alpha('#2e7d32', 0.16) },
    removed: { bgcolor: alpha('#d32f2f', 0.14) },
};
const MARK = { same: ' ', added: '+', removed: '-' };

/**
 * The check before "Mentés (felülírás)": what the overwrite changes in the stored UCon script, and
 * how many products share it. Replaces a bare confirm, so a shared script is not clobbered blind.
 */
const ScriptDiffDialog: React.FC<IScriptDiffDialogProps> = ({ open, title, saved, next, sharedBy, onCancel, onConfirm }) => {
    const lines = React.useMemo(
        () => (open && saved !== null ? diffLines(normalize(saved), normalize(next)) : []),
        [open, saved, next],
    );
    const added = lines.filter((l) => l.kind === 'added').length;
    const removed = lines.filter((l) => l.kind === 'removed').length;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                {sharedBy > 1 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Ezt a szkriptet {sharedBy} termék használja: a mentés mindegyiknél felülírja.
                    </Alert>
                )}
                {saved === null ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : added + removed === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nincs eltérés a mentett szkripthez képest.</Typography>
                ) : (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            A mentetthez képest: +{added} / −{removed} sor
                        </Typography>
                        <Box sx={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5,
                            maxHeight: '60vh', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            {lines.map((line, i) => line.kind === 'skip'
                                ? (
                                    <Box key={i} sx={{ px: 1, color: 'text.disabled', fontStyle: 'italic' }}>
                                        … {line.count} változatlan sor …
                                    </Box>
                                )
                                : (
                                    <Box key={i} sx={{ px: 1, whiteSpace: 'pre', ...LINE_SX[line.kind] }}>
                                        {MARK[line.kind]} {line.text}
                                    </Box>
                                ))}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Mégse</Button>
                <Button variant="contained" color={sharedBy > 1 ? 'warning' : 'primary'} disabled={saved === null}
                    onClick={onConfirm}>
                    Mentés (felülírás)
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScriptDiffDialog;
